import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTrial } from '../contexts/TrialContext';
import api from '../lib/api';
import { goPro } from '../lib/checkout';
import { BookOpen, Lock, CheckCircle, Play, ChevronRight } from 'lucide-react';

const SKILL_COLORS = { writing: '#EF4444', speaking: '#10B981', listening: '#3B82F6', reading: '#F59E0B' };

function CourseCard({ course, unlocked, entryLabel, onLocked }) {
  const color = SKILL_COLORS[course.skill_id] || '#6B7280';
  return (
    <Link to={unlocked ? `/courses/${course.slug}` : '#'}
      onClick={(e) => { if (!unlocked) { e.preventDefault(); onLocked(); } }}
      className={`bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 transition group ${unlocked ? 'hover:border-primary/30 hover:shadow-md active:bg-neutral-50' : 'opacity-70'}`}
      data-testid={`course-card-${course.slug}`}>
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}15`, color }}>
          <BookOpen className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-neutral-900 text-[15px] sm:text-base leading-snug">{course.title}</h3>
            {course.is_free
              ? <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-semibold tracking-wide" data-testid={`badge-free-${course.slug}`}>{entryLabel.toUpperCase()}</span>
              : <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-semibold tracking-wide" data-testid={`badge-pro-${course.slug}`}>PRO</span>}
            {!unlocked && <Lock size={14} className="text-neutral-400" />}
          </div>
          {course.description && <p className="text-xs sm:text-sm text-neutral-500 mb-2 sm:mb-3 line-clamp-2">{course.description}</p>}
          <div className="flex items-center gap-3 sm:gap-4 text-xs text-neutral-400">
            <span>{course.total_sections} sections</span>
            {course.progress_pct > 0 && (
              <span className="flex items-center gap-1 text-green-600"><CheckCircle size={12} />{course.progress_pct}% complete</span>
            )}
          </div>
          {course.progress_pct > 0 && (
            <div className="mt-2 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${course.progress_pct}%`, background: color }} />
            </div>
          )}
        </div>
        {unlocked && (
          <>
            <Play size={18} className="hidden sm:block text-neutral-300 group-hover:text-primary transition mt-1" />
            <ChevronRight size={18} className="sm:hidden text-neutral-300 mt-2.5 shrink-0" />
          </>
        )}
      </div>
    </Link>
  );
}

export default function Courses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const isPro = user?.subscription_status && user.subscription_status !== 'free';
  const { entryLabel, trialMode } = useTrial();
  const isAdmin = user?.role === 'admin';
  const hasProAccess = isPro || isAdmin;

  useEffect(() => {
    api.get('/api/courses').then(r => setCourses(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Backend `is_locked` is the source of truth; the flags are the fallback for old payloads.
  const isUnlocked = (course) => {
    if (typeof course.is_locked === 'boolean') return !course.is_locked;
    return course.is_free || course.unlocked || hasProAccess;
  };

  return (
    <DashboardLayout title="Courses">
      <div data-testid="courses-page">
        <p className="text-sm sm:text-base text-neutral-600 mb-4 sm:mb-6">Structured learning paths designed by CELPIP experts. Master each skill with video lessons and strategies.</p>

        {!hasProAccess && (
          <div className="mb-4 sm:mb-6 p-3.5 sm:p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="course-pro-banner">
            <div>
              <p className="text-sm font-medium text-amber-800">Some courses require a Pro subscription</p>
              <p className="text-xs text-amber-600 mt-0.5">{trialMode ? `${entryLabel} courses are included in your trial — Pro courses unlock with a subscription.` : 'Free courses are open to everyone — Pro courses unlock with a subscription.'}</p>
            </div>
            <button onClick={() => goPro(navigate)} className="w-full sm:w-auto shrink-0 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition" data-testid="courses-upgrade-btn">Upgrade to Pro</button>
          </div>
        )}

        {loading ? (
          <div className="grid md:grid-cols-2 gap-3 sm:gap-4">{[1,2,3,4].map(i => <div key={i} className="h-28 sm:h-40 bg-neutral-100 rounded-xl animate-pulse" />)}</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3 sm:gap-4" data-testid="courses-grid">
            {courses.map(course => (
              <CourseCard key={course.id} course={course} unlocked={isUnlocked(course)} entryLabel={entryLabel} onLocked={() => goPro(navigate)} />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
